import { Button, notification } from 'antd';
import { useState } from 'react';
import { useGenerateWaybillMutation } from 'redux/services/waybillApi';
import { useAppSelector } from 'redux/store';

interface GenerateWaybillsButtonProps {
  openData: {
    id?: string;
    requestNumber?: string;
    contactPhoneNumber?: string;
    contactName?: string;
    contactEmail?: string;
    defectiveReturns?: {
      contactName?: string;
      contactPhoneNumber?: string;
      contactEmail?: string;
      items?: Array<{
        lineNumber: number;
        productNumber: string;
        serialNumber: string;
        quantity: number;
        caseNumber?: string;
        rmaNumber?: string;
        used: boolean;
        notes?: string;
      }>;
    };
  }; // For posted orders (SparingOrder)
  onWaybillsGenerated?: () => void; // Callback to refresh parent data
  // We need the sparing request ID to update the defective returns
  sparingRequestId?: string;
}

export const GenerateWaybillsButton: React.FC<GenerateWaybillsButtonProps> = ({ openData, onWaybillsGenerated, sparingRequestId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [generateWaybill] = useGenerateWaybillMutation();
  const divisionId = useAppSelector((state) => state.app.acuityContext?.selectedCustomer?.id);

  // Check if phone number is valid for waybill generation
  const isPhoneValid = openData.contactPhoneNumber && openData.contactPhoneNumber.replace(/\D/g, '').length === 10;

  const handleGenerateWaybill = async (): Promise<void> => {
    // Check if order is posted (waybills can only be generated after posting to D365)
    const orderId = openData.id || openData.requestNumber;

    if (!orderId || orderId === '') {
      notification.error({
        message: 'Error',
        description: 'Cannot generate waybills: Order must be posted first',
        duration: 5
      });

      return;
    }

    // Validation
    if (!openData.contactPhoneNumber || openData.contactPhoneNumber.replace(/\D/g, '').length !== 10) {
      const phoneLength = openData.contactPhoneNumber?.replace(/\D/g, '').length || 0;

      notification.error({
        message: 'Phone Number Invalid',
        description: `Contact Phone Number must be exactly 10 digits (currently ${phoneLength}). Please update the phone number in Contact Information before generating waybills.`,
        duration: 8
      });

      return;
    }

    if (!openData.defectiveReturns?.items?.length) {
      notification.error({
        message: 'Error',
        description: 'No defective return items found',
        duration: 5
      });

      return;
    }

    setIsLoading(true);

    try {
      // API call to generate waybills
      const response = await generateWaybill({
        divisionId: divisionId || '',
        sparingRequestId: orderId
      }).unwrap();

      // If we have waybill data, log it for debugging
      if (response.waybills || response.packages) {
        // TODO: Backend needs to provide an endpoint to update waybill data
        // For now, we'll just log the data and call the callback
      }

      notification.success({
        message: 'Success',
        description: 'Waybills have been generated successfully',
        duration: 5
      });

      // Call the callback to refresh parent data
      if (onWaybillsGenerated) {
        onWaybillsGenerated();
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to generate waybills',
        duration: 5
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <Button
        loading={isLoading}
        onClick={handleGenerateWaybill}
        disabled={!isPhoneValid}
        style={{
          borderColor: isPhoneValid ? '#159d47' : '#d9d9d9',
          color: isPhoneValid ? '#159d47' : '#bfbfbf'
        }}
        title={!isPhoneValid ? 'Phone number must be exactly 10 digits to generate waybills' : 'Generate waybills for defective returns'}>
        Generate Waybills
      </Button>

      {!isPhoneValid && <div style={{ marginTop: 8, fontSize: '12px', color: '#faad14' }}>⚠️ Phone number must be exactly 10 digits to generate waybills</div>}
    </div>
  );
};
